/**
 * Client-Side-Router via History-API (Phase 3.5 §4 + §5 Shell-Refactor).
 *
 * NEU: Router rendert in den View-Container der App-Shell (`#view-container`),
 * nicht mehr in `#app` direkt. Shell-Lifecycle (Rail/Sub-Sidebar/Topbar) bleibt
 * persistent zwischen Routen — nur der innere Container wird neu gerendert.
 *
 * Login-Page läuft NICHT durch den Router — sie wird vor mountShell() von
 * main.ts direkt gerendert.
 */

import type { UserContext } from "../api/types";
import { redirectToLogin } from "../auth/login";
import { getViewContainer, setActiveRoute } from "../components/app-shell";

export type RouteParams = Record<string, string>;

export type RouteHandler = (
  host: HTMLElement,
  user: UserContext,
  params: RouteParams
) => Promise<void> | void;

export type LazyRoute = () => Promise<{ render: RouteHandler }>;

// =============================================================================
// User-Cache
// =============================================================================

let cachedUser: UserContext | null = null;

export function setCurrentUser(user: UserContext | null): void {
  cachedUser = user;
}

export function getCurrentUser(): UserContext | null {
  return cachedUser;
}

// =============================================================================
// Route-Registry
// =============================================================================

const routes: Array<{ pattern: string; loader: LazyRoute }> = [];

export function registerRoute(pattern: string, loader: LazyRoute): void {
  routes.push({ pattern, loader });
}

export function _resetRoutesForTests(): void {
  routes.length = 0;
}

// =============================================================================
// Pattern-Matcher
// =============================================================================

export function matchRoute(pattern: string, path: string): RouteParams | null {
  const patSegs = pattern.split("/").filter(Boolean);
  const pathSegs = path.split("/").filter(Boolean);
  if (patSegs.length !== pathSegs.length) return null;

  const params: RouteParams = {};
  for (let i = 0; i < patSegs.length; i++) {
    const ps = patSegs[i]!;
    const cs = pathSegs[i]!;
    if (ps.startsWith(":")) {
      params[ps.slice(1)] = decodeURIComponent(cs);
    } else if (ps.toLowerCase() !== cs.toLowerCase()) {
      return null;
    }
  }
  return params;
}

// =============================================================================
// navigate + renderRoute
// =============================================================================

export async function navigate(path: string): Promise<void> {
  if (window.location.pathname !== path) {
    window.history.pushState(null, "", path);
  }
  await renderRoute(path);
}

export async function renderRoute(path: string): Promise<void> {
  const user = getCurrentUser();
  if (!user) {
    redirectToLogin();
    return;
  }

  // Shell aktualisieren (Rail-Highlight + Sub-Sidebar + Breadcrumbs)
  setActiveRoute(path);

  const host = getViewContainer();

  for (const { pattern, loader } of routes) {
    const params = matchRoute(pattern, path);
    if (params) {
      try {
        const mod = await loader();
        await mod.render(host, user, params);
      } catch (err) {
        console.error(`[router] render ${pattern} failed:`, err);
        renderError(host, err);
      }
      // Top-of-View scroll bei jedem Render
      host.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }
  }

  render404(host, path);
}

// =============================================================================
// Link-Interception
// =============================================================================

export function attachLinkInterception(): void {
  document.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    const link = target?.closest("a");
    if (!link) return;

    const href = link.getAttribute("href");
    if (!href || !href.startsWith("/")) return;

    if (link.target === "_blank") return;
    if ((ev as MouseEvent).metaKey || (ev as MouseEvent).ctrlKey || (ev as MouseEvent).shiftKey)
      return;

    ev.preventDefault();
    void navigate(href);
  });
}

// =============================================================================
// Lifecycle
// =============================================================================

export function startRouter(): void {
  window.addEventListener("popstate", () => {
    void renderRoute(window.location.pathname);
  });
  attachLinkInterception();
  void renderRoute(window.location.pathname);
}

// =============================================================================
// 404 + Error-Renderer
// =============================================================================

function render404(host: HTMLElement, path: string): void {
  host.innerHTML = `
    <div class="state-error">
      <h2>404 — Seite nicht gefunden</h2>
      <p>Pfad <code>${escapeHtml(path)}</code> ist nicht registriert.</p>
      <p><a href="/" data-router-link>← Zurück zum Hub</a></p>
    </div>
  `;
}

function renderError(host: HTMLElement, err: unknown): void {
  const msg = err instanceof Error ? err.message : String(err);
  host.innerHTML = `
    <div class="state-error">
      <h2>Fehler beim Laden der View</h2>
      <p>${escapeHtml(msg)}</p>
      <p><a href="/" data-router-link>← Zurück zum Hub</a></p>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
